/**
 * 用途：Google 广告计划编辑页，支持对已有广告系列 / 广告组 / 广告的字段进行编辑
 * 路由：/campaigns/edit/google/:entity/:id （entity 取值 campaign | adgroup | ad）
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { MultiSelect } from '../components/MultiSelect';
import {
  accountsApi,
  type DraftPayloadRequest,
  type ValidateAdFullRequest,
  type GoogleEditAdPayload,
  type GoogleEditAdSetPayload,
  type GoogleEditCampaignPayload,
  type GoogleEditDetailResponse,
} from '../api/accounts';
import { useAppStore } from '../store/useAppStore';
import { confirmDialog } from '../store/useConfirmStore';
import { DateTimePicker, FormGroup, Hint, Label, SectionCard, WizardErrorBanner, type WizardStep } from './adWizardShared';

type ToastType = 'success' | 'error' | 'warning' | 'info';

const GOOGLE_OBJECTIVES = [
  { value: 'SALES', label: '销售' },
  { value: 'LEADS', label: '潜在客户' },
  { value: 'TRAFFIC', label: '网站流量' },
  { value: 'AWARENESS', label: '品牌认知' },
];
const GOOGLE_STATUS = [
  { value: 'ENABLED', label: '启用' },
  { value: 'PAUSED', label: '暂停' },
  { value: 'REMOVED', label: '已移除' },
];
const MATCH_TYPE_LABELS: Record<string, string> = {
  BROAD: '广泛匹配',
  PHRASE: '词组匹配',
  EXACT: '完全匹配',
};

type KeywordItem = { text: string; match_type?: string };
type GoogleEditDraftPayload = Record<string, unknown>;

function toPositiveNumber(v: string | number): number | undefined {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

function toIDList(v: string): string[] {
  return v
    .split(/[\n,;\s]+/g)
    .map((x) => x.trim())
    .filter((x) => /^\d+$/.test(x));
}

function compactTextList(list: string[]): string[] {
  return list.map((x) => x.trim()).filter(Boolean);
}

function isHttpUrl(v: string) {
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function parseJSONArray(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed.map((x) => String(x || '').trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function parseKeywords(raw: string): KeywordItem[] {
  try {
    const parsed = JSON.parse(raw || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.map((x) => {
      if (typeof x === 'string') return { text: x, match_type: 'PHRASE' };
      return { text: String(x?.text || ''), match_type: String(x?.match_type || 'PHRASE') };
    }).filter((x) => x.text.trim());
  } catch {
    return [];
  }
}

function formatDraftTimestamp(input?: string | Date | null) {
  if (!input) return '--';
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '--';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export default function GooglePlanEditPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { brandId, siteId, hasPermission, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const { entity, id } = useParams<{ entity: 'campaign' | 'adset' | 'ad'; id: string }>();
  const lockedSiteId = useRef(siteId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [editableFields, setEditableFields] = useState<string[]>([]);
  const [toast, setToast] = useState<{ msg: string; type: ToastType } | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const formCardRef = useRef<HTMLDivElement | null>(null);
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});

  const showToast = useCallback((msg: string, type: ToastType = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500);
  }, []);

  // Block site switching while editing
  useEffect(() => {
    if (siteId !== lockedSiteId.current) {
      showToast('编辑广告期间不允许切换站点', 'error');
      useAppStore.getState().selectSiteById(lockedSiteId.current);
    }
  }, [siteId]);

  const [campaign, setCampaign] = useState<GoogleEditCampaignPayload>({
    name: '', objective: '', campaign_status: 'PAUSED', daily_budget_micros: 0,
    start_date: '', end_date: '', target_google_search: true, target_search_network: true,
    target_partner_search_network: false, target_content_network: false,
  });
  const [adset, setAdset] = useState<GoogleEditAdSetPayload>({
    name: '', adset_status: 'PAUSED', cpc_bid_micros: 0, keywords_json: '[]', location_ids_json: '[]', language_id: '',
  });
  const [ad, setAd] = useState<GoogleEditAdPayload>({
    name: '', ad_status: 'PAUSED', final_url: '', headlines_json: '[]', descriptions_json: '[]', path1: '', path2: '',
  });

  const [keywords, setKeywords] = useState<KeywordItem[]>([]);
  const [locationIDsText, setLocationIDsText] = useState('');
  const [headlines, setHeadlines] = useState<string[]>(['', '', '']);
  const [descriptions, setDescriptions] = useState<string[]>(['', '']);
  const [accountID, setAccountID] = useState('');
  const numID = Number(id || '0');
  const draftKey = useMemo(() => `google:edit:${entity}:${numID}`, [entity, numID]);
  const initialDraftSnapshotRef = useRef<GoogleEditDraftPayload | null>(null);
  const savedDraftSnapshotRef = useRef<GoogleEditDraftPayload | null>(null);
  const draftLoadedRef = useRef(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftBootstrapVersion, setDraftBootstrapVersion] = useState(0);
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<string | null>(null);
  const [hasTempDraft, setHasTempDraft] = useState(false);
  const canEdit = (f: string) => editableFields.includes(f);
  const campaignPublished = useMemo(() => editableFields.length > 0 && !editableFields.includes('campaign.objective'), [editableFields]);
  const adsetPublished = useMemo(
    () => editableFields.length > 0 && (!editableFields.includes('adset.location_ids_json') || !editableFields.includes('adset.language_id')),
    [editableFields],
  );
  const adPublished = useMemo(() => editableFields.length > 0 && !editableFields.includes('ad.path1'), [editableFields]);
  const selfPublished = entity === 'campaign' ? campaignPublished : entity === 'adset' ? adsetPublished : adPublished;
  const draftFeatureEnabled = !selfPublished;

  const initialStep = (entity === 'campaign' ? 1 : entity === 'adset' ? 2 : 3) as WizardStep;
  const [step, setStep] = useState<WizardStep>(1);

  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  const maxVisibleStep = useMemo(() => {
    if (entity === 'campaign') return 1;
    if (entity === 'adset') return 2;
    return 3;
  }, [entity]);

  const visibleSteps = useMemo<WizardStep[]>(() => {
    if (entity === 'campaign') return [1];
    if (entity === 'adset') return [1, 2];
    return [1, 2, 3];
  }, [entity]);

  const canGoPrev = useMemo(() => {
    if (selfPublished) return false;
    if (entity === 'campaign') return false;
    if (entity === 'adset') return step > 1 && !campaignPublished;
    if (entity === 'ad') {
      if (adsetPublished) return false;
      if (step === 3) return true;
      if (step === 2) return !campaignPublished;
      return false;
    }
    return false;
  }, [selfPublished, entity, step, campaignPublished, adsetPublished]);

  const canGoNext = useMemo(() => {
    if (selfPublished) return false;
    return step < maxVisibleStep;
  }, [selfPublished, step, maxVisibleStep]);

  const keywordCount = useMemo(() => keywords.filter((k) => k.text.trim()).length, [keywords]);
  const headlineCount = useMemo(() => headlines.filter((h) => h.trim()).length, [headlines]);
  const descriptionCount = useMemo(() => descriptions.filter((d) => d.trim()).length, [descriptions]);

  const budgetDisplay = useMemo(() => {
    if (!campaign.daily_budget_micros) return '';
    return String(campaign.daily_budget_micros / 1_000_000);
  }, [campaign.daily_budget_micros]);

  const buildDraftPayload = useCallback((): GoogleEditDraftPayload => ({
    step,
    campaign,
    adset,
    ad,
    keywords,
    locationIDsText,
    headlines,
    descriptions,
    accountID,
  }), [step, campaign, adset, ad, keywords, locationIDsText, headlines, descriptions, accountID]);

  const applyDraftPayload = useCallback((payload: GoogleEditDraftPayload) => {
    if (payload.step === 1 || payload.step === 2 || payload.step === 3) setStep(payload.step);
    if (payload.campaign && typeof payload.campaign === 'object') setCampaign(payload.campaign as GoogleEditCampaignPayload);
    if (payload.adset && typeof payload.adset === 'object') setAdset(payload.adset as GoogleEditAdSetPayload);
    if (payload.ad && typeof payload.ad === 'object') setAd(payload.ad as GoogleEditAdPayload);
    if (Array.isArray(payload.keywords)) setKeywords(payload.keywords as KeywordItem[]);
    if (typeof payload.locationIDsText === 'string') setLocationIDsText(payload.locationIDsText);
    if (Array.isArray(payload.headlines)) setHeadlines(payload.headlines as string[]);
    if (Array.isArray(payload.descriptions)) setDescriptions(payload.descriptions as string[]);
    if (typeof payload.accountID === 'string') setAccountID(payload.accountID);
  }, []);

  const buildDraftRequest = useCallback((payload: GoogleEditDraftPayload): DraftPayloadRequest => ({
    platform: 'google',
    scene: 'edit',
    entity_type: entity || 'campaign',
    entity_id: numID,
    draft_key: draftKey,
    draft_name: `google-${entity}-${numID}`,
    site_id: siteId || undefined,
    payload,
  }), [draftKey, entity, numID, siteId]);

  const saveTempDraftSilently = useCallback(async () => {
    if (!brandId || !draftLoadedRef.current) return;
    try {
      const resp = await accountsApi.saveTempDraft(brandId, buildDraftRequest(buildDraftPayload()));
      setLastDraftSavedAt(resp.updated_at || new Date().toISOString());
      setHasTempDraft(true);
      return resp;
    } catch {
      // ignore autosave errors
      return null;
    }
  }, [brandId, buildDraftPayload, buildDraftRequest]);

  const clearTempDraftSilently = useCallback(async () => {
    if (!brandId) return;
    try {
      await accountsApi.deleteTempDraft({
        brand_id: brandId,
        site_id: siteId || undefined,
        platform: 'google',
        scene: 'edit',
        entity_type: entity || 'campaign',
        entity_id: numID,
        draft_key: draftKey,
      });
    } catch {
      // ignore cleanup errors
    }
  }, [brandId, draftKey, entity, numID, siteId]);

  const handleDiscardDraft = useCallback(async () => {
    if (!brandId || !initialDraftSnapshotRef.current) return;
    const ok = await confirmDialog({
      title: '放弃修改',
      message: '将删除当前临时草稿，并回退到上次已保存的草稿内容；如果没有已保存草稿，则回到进入页面时的内容。是否继续？',
      confirmText: '确定',
      danger: true,
    });
    if (!ok) return;
    await clearTempDraftSilently();
    applyDraftPayload(savedDraftSnapshotRef.current || initialDraftSnapshotRef.current);
    setLastDraftSavedAt(null);
    setHasTempDraft(false);
    showToast('已放弃临时草稿', 'success');
  }, [applyDraftPayload, brandId, clearTempDraftSilently, showToast]);

  const handleBackToList = useCallback(async () => {
    if (draftFeatureEnabled) await saveTempDraftSilently();
    navigate('/campaigns/google');
  }, [draftFeatureEnabled, navigate, saveTempDraftSilently]);

  useEffect(() => {
    if (!brandId || !entity || !Number.isFinite(numID) || numID <= 0) {
      setError('invalid route params');
      setLoading(false);
      return;
    }
    if (draftFeatureEnabled) {
      draftLoadedRef.current = false;
      setDraftReady(false);
      setLastDraftSavedAt(null);
      setHasTempDraft(false);
    }
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        let res: GoogleEditDetailResponse;
        if (entity === 'campaign') {
          res = await accountsApi.getGoogleCampaignEditDetail(brandId, numID, siteId);
        } else if (entity === 'adset') {
          res = await accountsApi.getGoogleAdSetEditDetail(brandId, numID, siteId);
        } else {
          res = await accountsApi.getGoogleAdEditDetail(brandId, numID, siteId);
        }
        setEditableFields(res.editable_fields || []);
        if (res.campaign) {
          setCampaign(res.campaign);
          setAccountID(res.campaign.account_id || '');
        }
        if (res.adset) {
          setAdset(res.adset);
          setKeywords(parseKeywords(res.adset.keywords_json || '[]'));
          setLocationIDsText(parseJSONArray(res.adset.location_ids_json || '[]').join(', '));
        }
        if (res.ad) {
          setAd(res.ad);
          setHeadlines(parseJSONArray(res.ad.headlines_json || '[]'));
          setDescriptions(parseJSONArray(res.ad.descriptions_json || '[]'));
        }
        if (draftFeatureEnabled) setDraftBootstrapVersion((prev) => prev + 1);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'load failed');
        if (draftFeatureEnabled) setDraftBootstrapVersion((prev) => prev + 1);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [brandId, draftFeatureEnabled, entity, numID]);

  useEffect(() => {
    if (!draftFeatureEnabled || !brandId || draftBootstrapVersion <= 0 || draftLoadedRef.current) return;
    let cancelled = false;

    (async () => {
      const basePayload = buildDraftPayload();
      initialDraftSnapshotRef.current = basePayload;
      savedDraftSnapshotRef.current = basePayload;

      try {
        const tempDraft = await accountsApi.getTempDraft({
          brand_id: brandId,
          site_id: siteId || undefined,
          platform: 'google',
          scene: 'edit',
          entity_type: entity || 'campaign',
          entity_id: numID,
          draft_key: draftKey,
        });
        if (!cancelled && tempDraft?.payload) {
          applyDraftPayload(tempDraft.payload as GoogleEditDraftPayload);
          draftLoadedRef.current = true;
          setDraftReady(true);
          setLastDraftSavedAt(tempDraft.updated_at || null);
          setHasTempDraft(true);
          showToast('已恢复临时草稿', 'info');
          return;
        }
      } catch {
        // ignore missing temp draft
      }

      try {
        const manualDraft = await accountsApi.getManualDraft({
          brand_id: brandId,
          site_id: siteId || undefined,
          platform: 'google',
          scene: 'edit',
          entity_type: entity || 'campaign',
          entity_id: numID,
          draft_key: draftKey,
        });
        if (!cancelled && manualDraft?.payload) {
          const payload = manualDraft.payload as GoogleEditDraftPayload;
          savedDraftSnapshotRef.current = payload;
          applyDraftPayload(payload);
          draftLoadedRef.current = true;
          setDraftReady(true);
          setLastDraftSavedAt(null);
          setHasTempDraft(false);
          showToast('已恢复保存草稿', 'info');
          return;
        }
      } catch {
        // ignore missing manual draft
      }

      if (!cancelled) {
        draftLoadedRef.current = true;
        setDraftReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyDraftPayload, brandId, buildDraftPayload, draftBootstrapVersion, draftFeatureEnabled, draftKey, entity, numID, showToast, siteId]);

  useEffect(() => {
    if (!draftFeatureEnabled || !brandId || !draftReady) return;
    const timer = window.setInterval(() => {
      void saveTempDraftSilently();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [brandId, draftFeatureEnabled, draftReady, saveTempDraftSilently]);

  const scrollFormToTop = () => {
    formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setFieldRef = (key: string) => (node: HTMLElement | null) => {
    fieldRefs.current[key] = node;
  };

  const scrollToField = (key?: string) => key && fieldRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  useEffect(() => {
    if (error && errorRef.current) errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const validateStep = (targetStep: WizardStep): string | null => {
    const errs: Record<string, string> = {};
    if (targetStep === 1) {
      if (!campaign.name.trim()) errs.campaignName = '请填写 Campaign 名称';
      if (!toPositiveNumber(campaign.daily_budget_micros / 1_000_000)) errs.dailyBudget = '请填写有效的日预算';
      if (!campaign.target_google_search && !campaign.target_search_network && !campaign.target_partner_search_network && !campaign.target_content_network) {
        errs.networks = '请至少启用一个搜索网络';
      }
    }
    if (targetStep === 2) {
      if (!adset.name.trim()) errs.adsetName = '请填写 AdSet 名称';
      const validKeywords = keywords.filter((k) => k.text.trim());
      if (validKeywords.length === 0) errs.keywords = '请至少填写一个关键词';
      const badKeyword = validKeywords.find((k) => k.text.trim().length > 80 || k.text.trim().split(/\s+/).length > 10);
      if (badKeyword) errs.keywords = '关键词不能超过 80 个字符且不能超过 10 个单词';
      if (adset.cpc_bid_micros && !toPositiveNumber(adset.cpc_bid_micros)) errs.cpcBid = '默认 CPC 出价必须大于 0';
      if (adset.language_id.trim() && !/^\d+$/.test(adset.language_id.trim())) errs.languageID = '语言 ID 必须为数字';
      const idsRaw = locationIDsText
        .split(/[\n,;\s]+/g)
        .map((x) => x.trim())
        .filter(Boolean);
      if (idsRaw.some((x) => !/^\d+$/.test(x))) errs.locationIDs = '地域 ID 必须为数字，多个值可使用逗号或空格分隔';
    }
    if (targetStep === 3) {
      if (!ad.name.trim()) errs.adName = '请填写 Ad 名称';
      if (!ad.final_url.trim()) errs.finalURL = '请填写 Final URL';
      if (ad.final_url.trim() && !isHttpUrl(ad.final_url.trim())) errs.finalURL = 'Final URL 必须使用 http 或 https';
      const hs = compactTextList(headlines);
      const ds = compactTextList(descriptions);
      if (hs.length < 3) errs.headlines = '请至少填写 3 个标题';
      if (ds.length < 2) errs.descriptions = '请至少填写 2 个描述';
      if (hs.length > 15) errs.headlines = '标题最多 15 个';
      if (ds.length > 4) errs.descriptions = '描述最多 4 个';
      if (hs.some((x) => x.length > 30)) errs.headlines = '单个标题不能超过 30 个字符';
      if (ds.some((x) => x.length > 90)) errs.descriptions = '单个描述不能超过 90 个字符';
      if (ad.path1.trim().length > 15) errs.path1 = 'Path 1 不能超过 15 个字符';
      if (ad.path2.trim().length > 15) errs.path2 = 'Path 2 不能超过 15 个字符';
      if (ad.path2.trim() && !ad.path1.trim()) errs.path2 = '填写 Path 2 时必须先填写 Path 1';
    }
    const keysByStep: Record<WizardStep, string[]> = {
      1: ['campaignName', 'dailyBudget', 'networks'],
      2: ['adsetName', 'cpcBid', 'locationIDs', 'languageID', 'keywords'],
      3: ['adName', 'finalURL', 'path1', 'path2', 'headlines', 'descriptions'],
    };
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const key of keysByStep[targetStep]) delete next[key];
      return { ...next, ...errs };
    });
    const first = keysByStep[targetStep].find((key) => errs[key]);
    if (first) {
      setError(errs[first]);
      setTimeout(() => scrollToField(first), 0);
      return errs[first];
    }
    return null;
  };

  const validateVisibleSteps = (): string | null => {
    for (const s of visibleSteps) {
      const err = validateStep(s);
      if (err) {
        setStep(s);
        return err;
      }
    }
    return null;
  };

  const handleNext = () => {
    if (selfPublished) {
      const err = validateStep(step);
      if (err) { setError(err); return; }
    } else {
      setFieldErrors((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) delete next[key];
        return next;
      });
    }
    setError('');
    if (step < 3) {
      setStep((s) => (s + 1) as WizardStep);
      scrollFormToTop();
    }
  };

  const handlePrev = () => {
    if (!canGoPrev) return;
    setError('');
    if (step > 1) {
      setStep((s) => (s - 1) as WizardStep);
      scrollFormToTop();
    }
  };

  const onSave = async () => {
    if (!entity || !brandId || numID <= 0) return;
    if (userLevel < 2) {
      showToast('只读角色无法保存修改', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:campaigns:edit')) {
      showToast('暂无编辑广告权限', 'error');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const normalizedCampaign: GoogleEditCampaignPayload = {
        ...campaign,
        daily_budget_micros: Number(campaign.daily_budget_micros) || 0,
      };
      const normalizedAdset: GoogleEditAdSetPayload = {
        ...adset,
        cpc_bid_micros: Number(adset.cpc_bid_micros) || 0,
        keywords_json: JSON.stringify(keywords.filter((k) => k.text.trim())),
        location_ids_json: JSON.stringify(locationIDsText.split(',').map((x) => x.trim()).filter(Boolean)),
      };
      const normalizedAd: GoogleEditAdPayload = {
        ...ad,
        headlines_json: JSON.stringify(compactTextList(headlines)),
        descriptions_json: JSON.stringify(compactTextList(descriptions)),
      };

      let result: GoogleEditDetailResponse;
      if (entity === 'campaign') {
        result = await accountsApi.updateGoogleCampaign(numID, { brand_id: brandId, site_id: siteId, campaign: normalizedCampaign });
      } else if (entity === 'adset') {
        result = await accountsApi.updateGoogleAdSet(numID, { brand_id: brandId, site_id: siteId, campaign: normalizedCampaign, adset: normalizedAdset });
      } else {
        result = await accountsApi.updateGoogleAd(numID, { brand_id: brandId, site_id: siteId, adset: normalizedAdset, ad: normalizedAd });
      }
      if (result.sync_mode === 'remote') {
        showToast('保存成功，已同步到 Google', 'success');
      } else {
        showToast('保存成功，仅更新本地内容', 'success');
      }
      if (draftFeatureEnabled) await clearTempDraftSilently();
      navigate('/campaigns/google');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    const err = validateVisibleSteps();
    if (err) { setError(err); return; }
    if (!accountID) {
      showToast('当前缺少 Google 账户 ID，无法执行远端校验', 'warning');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload: ValidateAdFullRequest = {
        platform: 'google',
        account_id: accountID,
        campaign_name: campaign.name.trim(),
        campaign_objective: campaign.objective,
        campaign_status: campaign.campaign_status,
        daily_budget: campaign.daily_budget_micros / 1_000_000,
        start_time: campaign.start_date || undefined,
        end_time: campaign.end_date || undefined,
        enable_google_search: campaign.target_google_search,
        enable_search_network: campaign.target_search_network,
        enable_partner_search_network: campaign.target_partner_search_network,
        enable_display_network: campaign.target_content_network,
        adset_name: adset.name.trim(),
        adset_status: adset.adset_status,
        cpc_bid: adset.cpc_bid_micros ? adset.cpc_bid_micros : undefined,
        keywords: keywords.map((k) => ({ text: k.text.trim(), match_type: k.match_type || 'PHRASE' })).filter((k) => k.text),
        location_ids: toIDList(locationIDsText),
        language_id: adset.language_id.trim() || undefined,
        ad_name: ad.name.trim(),
        ad_status: ad.ad_status,
        link: ad.final_url.trim(),
        path1: ad.path1.trim() || undefined,
        path2: ad.path2.trim() || undefined,
        headlines: compactTextList(headlines),
        descriptions: compactTextList(descriptions),
        headline: compactTextList(headlines)[0] || undefined,
        description: compactTextList(descriptions)[0] || undefined,
      };
      const vr = await accountsApi.validateAdFull(brandId, payload);
      if (!vr.valid) {
        if (vr.campaign_error) setStep(1);
        else if (vr.adset_error) setStep(2);
        else if (vr.creative_error) setStep(3);
        setError(vr.campaign_error || vr.adset_error || vr.creative_error || 'Google 校验失败');
      } else {
        showToast('Google 参数校验通过', 'success');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Google 校验失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={formCardRef} style={{ maxWidth: 980, margin: '0 auto', padding: isMobile ? '10px 12px 20px' : '20px 16px 32px' }}>
      <h2 style={{ marginBottom: 8 }}>Google 编辑</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button className="btn btn-outline" onClick={() => { void handleBackToList(); }} disabled={saving}>
          返回列表
        </button>
        {draftFeatureEnabled && (
          <>
            <button className="btn btn-outline" onClick={() => { void handleDiscardDraft(); }} disabled={saving}>
              放弃修改
            </button>
          </>
        )}
      </div>
      <Hint>实体：{entity} / ID: {id} · {selfPublished ? '已发布（不可编辑字段已置灰）' : '未发布（可编辑全部字段）'}</Hint>

      {/* Step progress bar — Facebook style */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', margin: '12px 0 22px', paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: entity === 'campaign' ? 220 : entity === 'adset' ? 420 : 640 }}>
          {visibleSteps.map((s, idx) => {
            const done = step > s;
            const active = step === s;
            return (
              <React.Fragment key={s}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                    background: done ? 'var(--green)' : active ? 'var(--primary)' : 'var(--surface3)',
                    color: done || active ? '#fff' : 'var(--text3)',
                  }}>{done ? '✓' : s}</div>
                  <div style={{ fontSize: 12, color: active ? 'var(--text)' : 'var(--text3)', fontWeight: active ? 700 : 500 }}>
                    {s === 1 ? '广告系列' : s === 2 ? '广告组' : '广告'}
                  </div>
                </div>
                {idx < visibleSteps.length - 1 && <div style={{ flex: 1, height: 1, background: step > s ? 'var(--green)' : 'var(--border)', margin: '0 12px' }} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {error && <WizardErrorBanner title="保存失败" message={error} bannerRef={errorRef} />}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          {/* ── Step 1: Campaign ─────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <SectionCard title="Google 搜索广告系列">
                <FormGroup>
                  <Label required>广告系列名称</Label>
                  <input ref={setFieldRef('campaignName') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.campaignName ? 'var(--red)' : undefined }} placeholder="例如：US_Search_Glasses_Q2" value={campaign.name} disabled={!canEdit('campaign.name')} onChange={(e) => { setCampaign((p) => ({ ...p, name: e.target.value })); setFieldErrors((prev) => ({ ...prev, campaignName: '' })); }} />
                  {fieldErrors.campaignName && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.campaignName}</div>}
                </FormGroup>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <FormGroup>
                    <Label>广告目标</Label>
                    <MultiSelect
                      single
                      options={GOOGLE_OBJECTIVES.map((o) => ({ value: o.value, label: o.label }))}
                      value={campaign.objective ? [campaign.objective] : []}
                      onChange={(vals) => setCampaign((p) => ({ ...p, objective: vals[0] ?? '' }))}
                      style={!canEdit('campaign.objective') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>广告系列状态</Label>
                    <MultiSelect
                      single
                      options={GOOGLE_STATUS.map((s) => ({ value: s.value, label: s.label }))}
                      value={campaign.campaign_status ? [campaign.campaign_status] : []}
                      onChange={(vals) => setCampaign((p) => ({ ...p, campaign_status: vals[0] ?? '' }))}
                      style={!canEdit('campaign.campaign_status') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>日预算</Label>
                    <input ref={setFieldRef('dailyBudget') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.dailyBudget ? 'var(--red)' : undefined }} type="number" min="0.01" step="0.01" value={budgetDisplay} disabled={!canEdit('campaign.daily_budget_micros')} onChange={(e) => { setCampaign((p) => ({ ...p, daily_budget_micros: Math.round((Number(e.target.value) || 0) * 1_000_000) })); setFieldErrors((prev) => ({ ...prev, dailyBudget: '' })); }} />
                    {fieldErrors.dailyBudget && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.dailyBudget}</div>}
                    <Hint>将映射到 Google Ads 广告系列预算的 `amount_micros`。</Hint>
                  </FormGroup>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <FormGroup>
                    <Label>开始时间</Label>
                    <DateTimePicker value={campaign.start_date} onChange={(v) => setCampaign((p) => ({ ...p, start_date: v }))} placeholder="立即开始（留空）" disabled={!canEdit('campaign.start_date')} />
                    <Hint>留空则按创建后立即开始处理。</Hint>
                  </FormGroup>
                  <FormGroup>
                    <Label>结束时间</Label>
                    <DateTimePicker value={campaign.end_date} onChange={(v) => setCampaign((p) => ({ ...p, end_date: v }))} placeholder="持续投放（留空）" disabled={!canEdit('campaign.end_date')} />
                    <Hint>留空则持续投放。</Hint>
                  </FormGroup>
                </div>
              </SectionCard>

              <SectionCard title="搜索网络设置">
                <div ref={setFieldRef('networks')} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={campaign.target_google_search} disabled={!canEdit('campaign.target_google_search')} onChange={(e) => setCampaign((p) => ({ ...p, target_google_search: e.target.checked }))} />
                    Google 搜索
                  </label>
                  <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={campaign.target_search_network} disabled={!canEdit('campaign.target_search_network')} onChange={(e) => setCampaign((p) => ({ ...p, target_search_network: e.target.checked }))} />
                    搜索网络
                  </label>
                  <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={campaign.target_partner_search_network} disabled={!canEdit('campaign.target_partner_search_network')} onChange={(e) => setCampaign((p) => ({ ...p, target_partner_search_network: e.target.checked }))} />
                    搜索合作伙伴网络
                  </label>
                  <label className="form-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" checked={campaign.target_content_network} disabled={!canEdit('campaign.target_content_network')} onChange={(e) => setCampaign((p) => ({ ...p, target_content_network: e.target.checked }))} />
                    内容网络
                  </label>
                </div>
                {fieldErrors.networks && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.networks}</div>}
                <Hint>至少保留一个网络开启，否则该广告系列无法正常投放。</Hint>
              </SectionCard>
            </div>
          )}

          {/* ── Step 2: AdSet ──────────────────────────────────────── */}
          {step === 2 && (
            <div>
              <SectionCard title="广告组设置">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <FormGroup>
                    <Label required>广告组名称</Label>
                    <input ref={setFieldRef('adsetName') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.adsetName ? 'var(--red)' : undefined }} value={adset.name} disabled={!canEdit('adset.name')} onChange={(e) => { setAdset((p) => ({ ...p, name: e.target.value })); setFieldErrors((prev) => ({ ...prev, adsetName: '' })); }} />
                    {fieldErrors.adsetName && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.adsetName}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>广告组状态</Label>
                    <MultiSelect
                      single
                      options={GOOGLE_STATUS.map((s) => ({ value: s.value, label: s.label }))}
                      value={adset.adset_status ? [adset.adset_status] : []}
                      onChange={(vals) => setAdset((p) => ({ ...p, adset_status: vals[0] ?? '' }))}
                      style={!canEdit('adset.adset_status') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>默认 CPC 出价</Label>
                    <input ref={setFieldRef('cpcBid') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.cpcBid ? 'var(--red)' : undefined }} type="number" min="0.01" step="0.01" value={String(adset.cpc_bid_micros || 0)} disabled={!canEdit('adset.cpc_bid_micros')} onChange={(e) => { setAdset((p) => ({ ...p, cpc_bid_micros: Number(e.target.value) || 0 })); setFieldErrors((prev) => ({ ...prev, cpcBid: '' })); }} />
                    {fieldErrors.cpcBid && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.cpcBid}</div>}
                    <Hint>将映射到 Google 广告组的 `cpc_bid_micros`。</Hint>
                  </FormGroup>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  <FormGroup>
                    <Label>地域 ID</Label>
                    <input ref={setFieldRef('locationIDs') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.locationIDs ? 'var(--red)' : undefined }} value={locationIDsText} disabled={!canEdit('adset.location_ids_json')} onChange={(e) => { setLocationIDsText(e.target.value); setFieldErrors((prev) => ({ ...prev, locationIDs: '' })); }} placeholder="2840, 21167" />
                    {fieldErrors.locationIDs && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.locationIDs}</div>}
                    <Hint>可选，直接填写 Google Ads 的 `geo_target_constant` ID；多个值可用逗号或空格分隔。</Hint>
                  </FormGroup>
                  <FormGroup>
                    <Label>语言 ID</Label>
                    <input ref={setFieldRef('languageID') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.languageID ? 'var(--red)' : undefined }} value={adset.language_id} disabled={!canEdit('adset.language_id')} onChange={(e) => { setAdset((p) => ({ ...p, language_id: e.target.value })); setFieldErrors((prev) => ({ ...prev, languageID: '' })); }} placeholder="1000" />
                    {fieldErrors.languageID && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.languageID}</div>}
                    <Hint>可选，直接填写 Google Ads 的 `language_constant` ID。</Hint>
                  </FormGroup>
                </div>
              </SectionCard>

              <SectionCard title={`关键词（${keywordCount}）`}>
                <div ref={setFieldRef('keywords')} />
                {keywords.map((k, idx) => (
                  <div key={`keyword-${idx}`} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <input className="form-input" style={{ flex: 1 }} placeholder="例如：蓝光眼镜" value={k.text} disabled={!canEdit('adset.keywords_json')} onChange={(e) => setKeywords((arr) => arr.map((item, i) => i === idx ? { ...item, text: e.target.value } : item))} />
                    <MultiSelect
                      single
                      style={{ width: isMobile ? '100%' : 120, ...(!canEdit('adset.keywords_json') ? { pointerEvents: 'none', opacity: 0.75 } : {}) }}
                      options={(['BROAD', 'PHRASE', 'EXACT'] as const).map((mt) => ({ value: mt, label: MATCH_TYPE_LABELS[mt] }))}
                      value={[(k.match_type || 'PHRASE')]}
                      onChange={(vals) => setKeywords((arr) => arr.map((item, i) => i === idx ? { ...item, match_type: vals[0] ?? '' } : item))}
                    />
                    <button type="button" className="btn btn-ghost" disabled={!canEdit('adset.keywords_json') || keywords.length <= 1} onClick={() => setKeywords((arr) => arr.filter((_, i) => i !== idx))}>删除</button>
                  </div>
                ))}
                <button type="button" className="btn btn-outline btn-sm" disabled={!canEdit('adset.keywords_json')} onClick={() => setKeywords((arr) => [...arr, { text: '', match_type: 'PHRASE' }])}>添加关键词</button>
                {fieldErrors.keywords && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{fieldErrors.keywords}</div>}
                <Hint>支持广泛、词组、完全匹配，Google Ads 的关键词限制会由后端再次校验。</Hint>
              </SectionCard>
            </div>
          )}

          {/* ── Step 3: Ad ────────────────────────────────────────── */}
          {step === 3 && (
            <div>
              <SectionCard title="响应式搜索广告">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <FormGroup>
                    <Label required>广告名称</Label>
                    <input ref={setFieldRef('adName') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.adName ? 'var(--red)' : undefined }} value={ad.name} disabled={!canEdit('ad.name')} onChange={(e) => { setAd((p) => ({ ...p, name: e.target.value })); setFieldErrors((prev) => ({ ...prev, adName: '' })); }} />
                    {fieldErrors.adName && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.adName}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>广告状态</Label>
                    <MultiSelect
                      single
                      options={GOOGLE_STATUS.map((s) => ({ value: s.value, label: s.label }))}
                      value={ad.ad_status ? [ad.ad_status] : []}
                      onChange={(vals) => setAd((p) => ({ ...p, ad_status: vals[0] ?? '' }))}
                      style={!canEdit('ad.ad_status') ? { pointerEvents: 'none', opacity: 0.75 } : undefined}
                    />
                  </FormGroup>
                </div>

                <FormGroup>
                  <Label required>最终网址</Label>
                  <input ref={setFieldRef('finalURL') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.finalURL ? 'var(--red)' : undefined }} placeholder="https://www.example.com" value={ad.final_url} disabled={!canEdit('ad.final_url')} onChange={(e) => { setAd((p) => ({ ...p, final_url: e.target.value })); setFieldErrors((prev) => ({ ...prev, finalURL: '' })); }} />
                  {fieldErrors.finalURL && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.finalURL}</div>}
                  <Hint>必须使用 `http` 或 `https`，编辑页会在保存前再次校验。</Hint>
                </FormGroup>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  <FormGroup>
                    <Label>路径 1</Label>
                    <input ref={setFieldRef('path1') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.path1 ? 'var(--red)' : undefined }} maxLength={15} value={ad.path1} disabled={!canEdit('ad.path1')} onChange={(e) => { setAd((p) => ({ ...p, path1: e.target.value })); setFieldErrors((prev) => ({ ...prev, path1: '', path2: prev.path2 })); }} />
                    {fieldErrors.path1 && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.path1}</div>}
                  </FormGroup>
                  <FormGroup>
                    <Label>路径 2</Label>
                    <input ref={setFieldRef('path2') as React.Ref<HTMLInputElement>} className="form-input" style={{ borderColor: fieldErrors.path2 ? 'var(--red)' : undefined }} maxLength={15} value={ad.path2} disabled={!canEdit('ad.path2')} onChange={(e) => { setAd((p) => ({ ...p, path2: e.target.value })); setFieldErrors((prev) => ({ ...prev, path2: '' })); }} />
                    {fieldErrors.path2 && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{fieldErrors.path2}</div>}
                  </FormGroup>
                </div>
              </SectionCard>

              <SectionCard title={`标题（${headlineCount}/15）`}>
                <div ref={setFieldRef('headlines')} />
                {headlines.map((h, idx) => (
                  <div key={`headline-${idx}`} style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <input className="form-input" style={{ flex: 1, minWidth: 0 }} maxLength={30} placeholder={`标题 ${idx + 1}`} value={h} disabled={!canEdit('ad.headlines_json')} onChange={(e) => setHeadlines((arr) => arr.map((item, i) => i === idx ? e.target.value : item))} />
                    <button type="button" className="btn btn-ghost" disabled={!canEdit('ad.headlines_json') || headlines.length <= 3} onClick={() => setHeadlines((arr) => arr.filter((_, i) => i !== idx))}>删除</button>
                  </div>
                ))}
                {headlines.length < 15 && (
                  <button type="button" className="btn btn-outline btn-sm" disabled={!canEdit('ad.headlines_json')} onClick={() => setHeadlines((arr) => [...arr, ''])}>添加标题</button>
                )}
                {fieldErrors.headlines && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{fieldErrors.headlines}</div>}
                <Hint>Google RSA 至少需要 3 个标题，单个标题不超过 30 字符。</Hint>
              </SectionCard>

              <SectionCard title={`描述（${descriptionCount}/4）`}>
                <div ref={setFieldRef('descriptions')} />
                {descriptions.map((d, idx) => (
                  <div key={`description-${idx}`} style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                    <input className="form-input" style={{ flex: 1, minWidth: 0 }} maxLength={90} placeholder={`描述 ${idx + 1}`} value={d} disabled={!canEdit('ad.descriptions_json')} onChange={(e) => setDescriptions((arr) => arr.map((item, i) => i === idx ? e.target.value : item))} />
                    <button type="button" className="btn btn-ghost" disabled={!canEdit('ad.descriptions_json') || descriptions.length <= 2} onClick={() => setDescriptions((arr) => arr.filter((_, i) => i !== idx))}>删除</button>
                  </div>
                ))}
                {descriptions.length < 4 && (
                  <button type="button" className="btn btn-outline btn-sm" disabled={!canEdit('ad.descriptions_json')} onClick={() => setDescriptions((arr) => [...arr, ''])}>添加描述</button>
                )}
                {fieldErrors.descriptions && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{fieldErrors.descriptions}</div>}
                <Hint>Google RSA 至少需要 2 个描述，单个描述不超过 90 字符。</Hint>
              </SectionCard>
            </div>
          )}

          {/* ── Navigation ───────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: 16 }}>
            <button className="btn btn-ghost" style={{ visibility: canGoPrev ? 'visible' : 'hidden' }} onClick={handlePrev} disabled={saving}>
              ← 上一步
            </button>
            {draftFeatureEnabled && hasTempDraft ? (
              <div style={{ display: 'grid', justifyItems: 'center', gap: 4, flex: '1 1 240px', minWidth: 220 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>已启用自动保存内容</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>最近保存时间：{formatDraftTimestamp(lastDraftSavedAt)}</div>
              </div>
            ) : <div style={{ flex: '1 1 240px' }} />}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {step === 3 && entity === 'ad' && (
                <button className="btn btn-outline" onClick={handleValidate} disabled={saving}>
                  校验
                </button>
              )}
              <button className="btn btn-outline" onClick={() => { void handleBackToList(); }} disabled={saving}>关闭</button>
              {canGoNext && (
                <button className="btn btn-primary" onClick={handleNext} disabled={saving}>
                  下一步 →
                </button>
              )}
              {!canGoNext && (
                <button className="btn btn-primary" onClick={onSave} disabled={saving}>
                  {saving ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="spinner" style={{ width: 12, height: 12, borderColor: 'rgba(0,0,0,.2)', borderTopColor: '#fff' }} />
                      保存中...
                    </span>
                  ) : '保存修改'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {toast && (
        <div style={{ position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, pointerEvents: 'none' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: toast.type === 'success' ? 'rgba(34,197,94,.06)' : toast.type === 'error' ? 'rgba(239,68,68,.06)' : toast.type === 'warning' ? 'rgba(245,158,11,.06)' : 'var(--surface2)',
            border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,.35)' : toast.type === 'error' ? 'rgba(239,68,68,.35)' : toast.type === 'warning' ? 'rgba(245,158,11,.35)' : 'var(--border2)'}`,
            color: toast.type === 'success' ? 'var(--green)' : toast.type === 'error' ? 'var(--red)' : toast.type === 'warning' ? 'var(--amber)' : 'var(--text)',
            borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 500,
            boxShadow: '0 8px 32px rgba(0,0,0,.45)', minWidth: 260, maxWidth: 460,
          }}>
            {toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
